import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import type { JSX } from 'react';
import useAssumeToViewObject, { type LookupObjectResponse } from '@/hooks/use-assume-to-view-object';
import useSession from '@/hooks/use-session';
type TenantSwitcherProps = {
  children: React.ReactNode;
  /** Id of the object to resolve (playbook / agent / entity). When absent, nothing is assumed. */
  id?: string;
  /** Shown (to firm employees) while the owning tenant is being resolved/assumed. */
  Loading: () => JSX.Element;
  /**
   * Runs once the assume completes. `didSwitch` is true when we actually changed tenants —
   * e.g. rewrite the route to the canonical resource, or hard-refresh an imperatively-loaded page.
   */
  onSwitched?: (data: LookupObjectResponse, didSwitch: boolean) => void;
};

/**
 * Wraps a resource page so that, when a firm employee opens a resource owned by a tenant they
 * aren't currently assuming, we resolve the owning tenant (via `/private/lookup_object`) and
 * auto-assume it. `Loading` renders in place of `children` while that's in flight.
 */
const TenantSwitcher = t0 => {
  const $ = _c(4);
  const {
    children,
    id,
    Loading,
    onSwitched
  } = t0;
  const {
    data: t1
  } = useSession();
  const {
    user
  } = t1;
  const isFirmEmployee = !!user?.isFirmEmployee;
  const {
    isPending,
    isSuccess
  } = useAssumeToViewObject(id, onSwitched);
  if (isFirmEmployee && id && (isPending || isSuccess)) {
    let t2;
    if ($[0] !== Loading) {
      t2 = <Loading />;
      $[0] = Loading;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== children) {
    t2 = <>{children}</>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export default TenantSwitcher;